'use client'

import { StaticPageList } from '@/components/static-pages/StaticPageList'
import { useToast } from '@/hooks/use-toast'
import apiClient from '@/lib/apiClient'
import type { StaticPage } from '@/types/static-page'
import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

const StaticPagesPage: React.FC = () => {
  const { toast } = useToast()
  const navigate = useNavigate()
  const [pages, setPages] = useState<StaticPage[]>([])
  const [isLoading, setIsLoading] = useState(false)

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(0)

  // Filter states
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')

  // Load static pages with filters and pagination
  const loadPages = useCallback(
    async (page: number = 1, filters?: { search?: string; status?: string }) => {
      setIsLoading(true)
      try {
        const params: Record<string, string | number> = {
          page,
          per_page: itemsPerPage,
        }

        if (filters?.search) {
          params.search = filters.search
        }

        if (filters?.status && filters.status !== 'all') {
          params.status = filters.status
        }

        const response = await apiClient.getStaticPages(params)
        setPages(response.data || [])
        setTotalItems(response.meta?.total || 0)
        setTotalPages(response.meta?.last_page || 1)
        setCurrentPage(response.meta?.current_page || 1)
      } catch (error) {
        console.error('Error loading static pages:', error)
        if (error && typeof error === 'object' && 'status' in error && error.status !== 404) {
          toast({
            title: 'Error',
            description: 'Failed to load static pages. Please try again.',
            variant: 'destructive',
          })
        }
        setPages([])
        setTotalItems(0)
        setTotalPages(1)
        setCurrentPage(1)
      } finally {
        setIsLoading(false)
      }
    },
    [itemsPerPage, toast]
  )

  // Effect to handle data loading on filter/search changes
  useEffect(() => {
    if (searchTerm) {
      const timer = setTimeout(() => {
        setCurrentPage(1)
        loadPages(1, {
          search: searchTerm,
          status: statusFilter !== 'all' ? statusFilter : undefined,
        })
      }, 500)
      return () => clearTimeout(timer)
    }

    setCurrentPage(1)
    loadPages(1, {
      search: undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
    })
  }, [searchTerm, statusFilter, loadPages])

  const handleSearchChange = (value: string) => {
    setSearchTerm(value)
  }

  const handleStatusFilterChange = (value: string) => {
    setStatusFilter(value)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    loadPages(page, {
      search: searchTerm || undefined,
      status: statusFilter !== 'all' ? statusFilter : undefined,
    })
  }

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage)
    setCurrentPage(1)
  }

  const handleAddPage = () => {
    navigate('/static-pages/add')
  }

  const handleEditPage = (page: StaticPage) => {
    navigate(`/static-pages/${page.id}`)
  }

  const handleDeletePage = async (id: string) => {
    try {
      await apiClient.deleteStaticPage(id)
      toast({
        title: 'Success',
        description: 'Static page deleted successfully',
      })
      loadPages(currentPage, {
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to delete static page. Please try again.',
        variant: 'destructive',
      })
    }
  }

  const handleToggleStatus = async (id: string) => {
    try {
      await apiClient.toggleStaticPageStatus(id)
      toast({
        title: 'Success',
        description: 'Static page status updated successfully',
      })
      loadPages(currentPage, {
        search: searchTerm || undefined,
        status: statusFilter !== 'all' ? statusFilter : undefined,
      })
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to update static page status. Please try again.',
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Static Pages</h1>
        <p className="text-muted-foreground">
          Manage static content pages like policies, terms, and information
        </p>
      </div>

      <StaticPageList
        pages={pages}
        isLoading={isLoading}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        searchTerm={searchTerm}
        statusFilter={statusFilter}
        onSearchChange={handleSearchChange}
        onStatusFilterChange={handleStatusFilterChange}
        onPageChange={handlePageChange}
        onItemsPerPageChange={handleItemsPerPageChange}
        onAddPage={handleAddPage}
        onEditPage={handleEditPage}
        onDeletePage={handleDeletePage}
        onToggleStatus={handleToggleStatus}
        onRefresh={() =>
          loadPages(currentPage, {
            search: searchTerm || undefined,
            status: statusFilter !== 'all' ? statusFilter : undefined,
          })
        }
      />
    </div>
  )
}

export default StaticPagesPage
